import useResendPassword from "../hooks/useResendPassword.hook";
import React, { useCallback, useRef } from "react";
import { RapButton } from "@dynata/rex-raptor";
import useDeleteUser from "../hooks/useDeleteUser.hook";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GENERIC_ERROR } from "core/utils/baseError";
import { Popup } from "core/widgets/popup.widget";
import { RiErrorWarningLine } from "react-icons/ri";
import type User from "models/user";
import type Account from "models/account";
import { ToastConfig } from "core/widgets/toastConfig.widget";

type ManagePendingInviteRecordProps = {
    user: User;
    organizationAccounts: Account[];
};

const ManagePendingInviteRecord: React.FC<ManagePendingInviteRecordProps> = ({
    user,
    organizationAccounts
}) => {
    const { mutate: mutateResendPassword, isError: resendPasswordIsError, isLoading: resendPasswordIsLoading, isSuccess: resendPasswordIsSuccess } = useResendPassword();
    const modalRef = useRef<HTMLDialogElement>(null);

    const handleResend = useCallback(async () => {
        mutateResendPassword(user.username, {
            onSuccess: () => {
                toast.success("An email will be sent shortly with a new temporary password", ToastConfig);
            },
            onError: (_) => {
                toast.error(GENERIC_ERROR, ToastConfig)
            }
        });
    }, []);

    const deleteUserMutation = useDeleteUser();

    const onDeleteUser = useCallback(() => {
        deleteUserMutation.mutate(user.id, {
            onSuccess: () => {
                toast.success("User deleted successfully", ToastConfig);
            },
            onError: () => {
                toast.error(GENERIC_ERROR, ToastConfig)
            }
        });
    }, [user.id]);

    const handleDeleteClick = useCallback(() => {
        if (modalRef.current) {
            modalRef.current.showModal();
        }
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (modalRef.current) {
            modalRef.current?.close();
        }
        onDeleteUser();
    }, []);

    const handleCancelDelete = useCallback(() => {
        if (modalRef.current) {
            modalRef.current?.close();
        }
    }, []);

    return (
        <>
            <Popup
                ref={modalRef}
                description={
                    <>
                        Are you sure you want to delete invite for this user <strong>{user.username}</strong>?
                    </>
                }
                onAccept={handleCancelDelete}
                onAcceptMessage="Cancel"
                onReject={handleConfirmDelete}
                onRejectMessage="Confirm"
                icon={<RiErrorWarningLine />}
            />
            <tr key={user.id}>
                <td>
                    {user.firstName} {user.lastName}
                </td>
                <td>{user.username}</td>
                <td>
                    {user.isAdmin ?
                        <React.Fragment>
                            <span className="role_tag">Organization Owner</span>
                        </React.Fragment>
                        : organizationAccounts.length > 1 ? (
                            user.roles.map((role, idx) => (
                                <React.Fragment key={idx}>
                                    <span className="role_tag"><b>{role.accountName}</b> ({role.role})</span>
                                </React.Fragment>
                            ))
                        ) : organizationAccounts.length === 1 ? (
                            <React.Fragment>
                                <span className="role_tag">{user.roles.at(0)?.role}</span>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <span className="no_account_tag">No accounts</span>
                            </React.Fragment>
                        )}
                </td>
                <td>
                    <p className="Button flex cursor-pointer">
                        <div className="btn-container">
                            <RapButton type="button" onClick={handleDeleteClick} styleType="link" size="small" color="danger">
                                Cancel Invite
                            </RapButton>
                            <RapButton type="button" styleType="link" size="small" color="main" onClick={handleResend}>
                                Resend Invite
                            </RapButton>
                        </div>
                    </p>
                </td>
            </tr>
        </>
    );
};

export default ManagePendingInviteRecord;
